import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../assets/css/formulario.css';
import CustomNavbar from './Navbar/Navbar';
import PuestoSelect from './PuestoSelect';
import { validateField, validateAllFields, ErrorMessage, SuccessMessage, validarFechasExperiencia } from './ValidationUtils';
import axios from 'axios';
import { useTr } from '../hooks/useTr';

const MESES = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
];

const generarAnios = () => {
    const anioActual = new Date().getFullYear();
    const anios = [];
    for (let i = anioActual; i >= 1970; i--) {
        anios.push(i);
    }
    return anios;
};

const formacionVacia = () => ({
    titulo: '',
    tipoEstudio: '',
    areaEstudio: '',
    institucion: '',
    estadoEstudio: '',
    fechaInicioEstudio: '',
    fechaFinEstudio: ''
});

const idiomaVacio = () => ({
    idioma: '',
    otroIdioma: '',
    nivelEscrito: '',
    nivelOral: ''
});

const experienciaVacia = () => ({
    empresa: '',
    actividadEmpresa: '',
    puesto: '',
    otroPuesto: '',
    nivelExperiencia: '',
    paisEmpresa: '',
    fechaInicioExperiencia: { mes: '', año: '' },
    fechaFinalizacionExperiencia: { mes: '', año: '' },
    descripcionResponsabilidad: '',
    personasCargo: ''
});

const formularioVacio = () => ({
    nombre: '',
    apellido: '',
    nacionalidad: '',
    fechaNacimiento: '',
    estadoCivil: '',
    tipoDocumento: '',
    cedula: '',
    genero: '',
    telefono: '',
    provincia: '',
    canton: '',
    direccion: '',
    email: '',
    preferenciaSalarial: '',
    cargo: '',
    cargoData: { puesto: '', otroPuesto: '' },
    discapacidad: '',
    porcentajeDiscapacidad: '',
    trabajado_conduto: '',
    formacionesAcademicas: [formacionVacia()],
    idiomas: [idiomaVacio()],
    experiencias: [experienciaVacia()],
    cv: ''
});

const Formulario = () => {
    const tr = useTr();
    const [paises, setPaises] = useState([]);
    const [formData, setFormData] = useState(formularioVacio());
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        axios.get('https://api-toolsconduto.conduto.com.ec/candidatos/countries')
            .then(response => {
                setPaises(response.data);
            })
            .catch(error => {
                console.error('Error fetching the country data:', error);
            });
    }, []);

    const fe = (key, className = 'invalid-feedback') =>
        errors[key] ? <div className={className}>{tr(errors[key])}</div> : null;

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;

        if (errors[name] || errors.submit) {
            setErrors(prev => {
                const newPrev = { ...prev };
                if (newPrev[name]) delete newPrev[name];
                if (newPrev.submit) delete newPrev.submit;
                return newPrev;
            });
        }

        if (name === 'discapacidad') {
            setFormData(prevState => ({
                ...prevState,
                discapacidad: value,
                porcentajeDiscapacidad: value === 'Sí' ? prevState.porcentajeDiscapacidad : ''
            }));

            if (value !== 'Sí' && (errors.porcentajeDiscapacidad || errors.submit)) {
                setErrors(prev => {
                    const newPrev = { ...prev };
                    delete newPrev.porcentajeDiscapacidad;
                    delete newPrev.submit;
                    return newPrev;
                });
            }

            return;
        }

        if (type === 'file') {
            setFormData(prevState => ({
                ...prevState,
                [name]: files[0]
            }));
        } else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value
            }));
        }

        const fieldError = validateField(name, value);
        if (fieldError) {
            setErrors(prev => ({ ...prev, [name]: fieldError }));
        }
    };

    const handleFormacionChange = (index, field, value) => {
        const errorKey = `formacion_${index}_${field}`;
        if (errors[errorKey]) {
            setErrors(prev => ({ ...prev, [errorKey]: '' }));
        }

        const updatedFormaciones = formData.formacionesAcademicas.map((formacion, i) => {
            if (i === index) {
                const newFormacion = { ...formacion, [field]: value };

                if (field === 'fechaInicioEstudio' || field === 'fechaFinEstudio') {
                    const fechaInicio = field === 'fechaInicioEstudio' ? value : formacion.fechaInicioEstudio;
                    const fechaFin = field === 'fechaFinEstudio' ? value : formacion.fechaFinEstudio;

                    if (fechaInicio && fechaFin) {
                        const yearInicio = parseInt(fechaInicio);
                        const yearFin = parseInt(fechaFin);
                        const currentYear = new Date().getFullYear();

                        if (yearFin > currentYear) {
                            setErrors(prev => ({
                                ...prev,
                                [`formacion_${index}_fechaFinEstudio`]: 'El año de fin no puede ser mayor al año actual'
                            }));
                        } else if (yearInicio > yearFin) {
                            setErrors(prev => ({
                                ...prev,
                                [`formacion_${index}_${field}`]: 'El año de inicio no puede ser mayor al año de finalización'
                            }));
                        }
                    }
                }

                return newFormacion;
            }
            return formacion;
        });

        setFormData(prevState => ({
            ...prevState,
            formacionesAcademicas: updatedFormaciones
        }));
    };

    const agregarFormacion = () => {
        setFormData(prevState => ({
            ...prevState,
            formacionesAcademicas: [...prevState.formacionesAcademicas, formacionVacia()]
        }));
    };

    const eliminarFormacion = (index) => {
        if (formData.formacionesAcademicas.length > 1) {
            const updatedFormaciones = formData.formacionesAcademicas.filter((_, i) => i !== index);
            setFormData(prevState => ({
                ...prevState,
                formacionesAcademicas: updatedFormaciones
            }));
        }
    };

    const handleRadioChange = (e) => {
        const { name, value } = e.target;

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        const normalizedValue = value.toLowerCase();

        setFormData(prevState => ({
            ...prevState,
            [name]: normalizedValue
        }));
    };


    const handleCargoChange = (data) => {
        setErrors(prev => {
            const next = { ...prev };
            delete next.cargo;
            delete next.submit;
            return next;
        });

        const texto = data.puesto === 'Otros' ? (data.otroPuesto || '') : data.puesto;

        setFormData(prevState => ({
            ...prevState,
            cargoData: data,
            cargo: texto.trim().slice(0, 100)
        }));
    };

    const handleIdiomaChange = (index, field, value) => {
        const errorKey = `idioma_${index}_${field}`;
        if (errors[errorKey]) {
            setErrors(prev => ({ ...prev, [errorKey]: '' }));
        }

        const updatedIdiomas = formData.idiomas.map((idioma, i) => {
            if (i === index) {
                return { ...idioma, [field]: value };
            }
            return idioma;
        });

        setFormData(prevState => ({
            ...prevState,
            idiomas: updatedIdiomas
        }));
    };

    const agregarIdioma = () => {
        setFormData(prevState => ({
            ...prevState,
            idiomas: [...prevState.idiomas, idiomaVacio()]
        }));
    };

    const eliminarIdioma = (index) => {
        if (formData.idiomas.length > 1) {
            const updatedIdiomas = formData.idiomas.filter((_, i) => i !== index);
            setFormData(prevState => ({
                ...prevState,
                idiomas: updatedIdiomas
            }));
        }
    };

    const handleExperienciaFechaChange = (index, tipoFecha, campo, value) => {
        const fieldKey = tipoFecha === 'inicio' ? 'fechaInicioExperiencia' : 'fechaFinalizacionExperiencia';

        const updatedExperiencias = formData.experiencias.map((experiencia, i) => {
            if (i === index) {
                const nuevaFecha = {
                    ...experiencia[fieldKey],
                    [campo]: value
                };

                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`experiencia_${index}_fechaInicio`];
                    delete newErrors[`experiencia_${index}_fechaFinalizacion`];

                    if (value) {
                        const specificKey = `experiencia_${index}_` + (tipoFecha === 'inicio' ? (campo === 'mes' ? 'fechaInicioMes' : 'fechaInicioAnio') : (campo === 'mes' ? 'fechaFinMes' : 'fechaFinAnio'));
                        delete newErrors[specificKey];
                    }

                    if (newErrors.submit) delete newErrors.submit;
                    return newErrors;
                });

                if (campo === 'mes' && !value) {
                    setErrors(prev => ({
                        ...prev,
                        [`experiencia_${index}_${tipoFecha === 'inicio' ? 'fechaInicioMes' : 'fechaFinMes'}`]: 'El mes es requerido'
                    }));
                }

                if (campo === 'año' && !value) {
                    setErrors(prev => ({
                        ...prev,
                        [`experiencia_${index}_${tipoFecha === 'inicio' ? 'fechaInicioAnio' : 'fechaFinAnio'}`]: 'El año es requerido'
                    }));
                }

                if (tipoFecha === 'inicio' && experiencia.fechaFinalizacionExperiencia.mes && experiencia.fechaFinalizacionExperiencia.año) {
                    const error = validarFechasExperiencia(nuevaFecha, experiencia.fechaFinalizacionExperiencia);
                    if (error) {
                        setErrors(prev => ({
                            ...prev,
                            [`experiencia_${index}_fechaInicio`]: error
                        }));
                    }
                }

                if (tipoFecha === 'fin' && experiencia.fechaInicioExperiencia.mes && experiencia.fechaInicioExperiencia.año) {
                    const error = validarFechasExperiencia(experiencia.fechaInicioExperiencia, nuevaFecha);
                    if (error) {
                        setErrors(prev => ({
                            ...prev,
                            [`experiencia_${index}_fechaFinalizacion`]: error
                        }));
                    }
                }

                return {
                    ...experiencia,
                    [fieldKey]: nuevaFecha
                };
            }
            return experiencia;
        });

        setFormData(prevState => ({
            ...prevState,
            experiencias: updatedExperiencias
        }));
    };

    const handleExperienciaChange = (index, field, value) => {
        const determineErrorKey = (f) => f === 'puestoData' ? `experiencia_${index}_puesto` : `experiencia_${index}_${f}`;
        const errorKey = determineErrorKey(field);

        setErrors(prev => {
            const next = { ...prev };
            if (next[errorKey]) delete next[errorKey];
            if (field === 'puestoData') {
                delete next[`experiencia_${index}_otroPuesto`];
            }
            if (next.submit) delete next.submit;
            return next;
        });

        const updatedExperiencias = formData.experiencias.map((experiencia, i) => {
            if (i === index) {
                if (field === 'puestoData') {
                    return {
                        ...experiencia,
                        puesto: value.puesto,
                        otroPuesto: value.otroPuesto
                    };
                }
                return { ...experiencia, [field]: value };
            }
            return experiencia;
        });

        setFormData(prevState => ({
            ...prevState,
            experiencias: updatedExperiencias
        }));
    };

    const agregarExperiencia = () => {
        setFormData(prevState => ({
            ...prevState,
            experiencias: [...prevState.experiencias, experienciaVacia()]
        }));
    };

    const eliminarExperiencia = (index) => {
        if (formData.experiencias.length > 1) {
            const updatedExperiencias = formData.experiencias.filter((_, i) => i !== index);
            setFormData(prevState => ({
                ...prevState,
                experiencias: updatedExperiencias
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateAllFields(formData);
        if (!formData.cargo) {
            validationErrors.cargo = 'El cargo es requerido';
        }
        if (Object.keys(validationErrors).length > 0 || !acceptedTerms) {
            const newErrors = { ...validationErrors };
            if (!acceptedTerms) {
                newErrors.terms = 'Debe aceptar los términos y condiciones';
            }
            if (Object.keys(validationErrors).length > 0) {
                newErrors.submit = 'Hay campos vacíos o con problemas de formato';
            }
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            const experienciasFormateadas = formData.experiencias.map(exp => ({
                ...exp,
                fechaInicioExperiencia: exp.fechaInicioExperiencia.año && exp.fechaInicioExperiencia.mes
                    ? `${exp.fechaInicioExperiencia.año}-${exp.fechaInicioExperiencia.mes}`
                    : '',
                fechaFinalizacionExperiencia: exp.fechaFinalizacionExperiencia.año && exp.fechaFinalizacionExperiencia.mes
                    ? `${exp.fechaFinalizacionExperiencia.año}-${exp.fechaFinalizacionExperiencia.mes}`
                    : ''
            }));

            const submitData = new FormData();

            const simpleFields = [
                'nombre', 'apellido', 'nacionalidad', 'fechaNacimiento', 'estadoCivil',
                'tipoDocumento', 'cedula', 'genero', 'telefono', 'provincia', 'canton',
                'direccion', 'email', 'cargo', 'preferenciaSalarial', 'discapacidad', 'porcentajeDiscapacidad',
                'trabajado_conduto'
            ];

            simpleFields.forEach(field => {
                if (formData[field] !== undefined && formData[field] !== null) {
                    submitData.append(field, formData[field].toString());
                }
            });

            submitData.append('autoriza', acceptedTerms ? '1' : '0');

            submitData.append('idiomas', JSON.stringify(formData.idiomas || []));
            submitData.append('formacionesAcademicas', JSON.stringify(formData.formacionesAcademicas || []));
            submitData.append('experiencias', JSON.stringify(experienciasFormateadas || []));

            if (formData.cv) {
                submitData.append('cv', formData.cv);
            }

            const response = await fetch('https://api-toolsconduto.conduto.com.ec/candidatos/guardar-hoja-vida', {
                method: 'POST',
                body: submitData
            });

            const result = await response.json();

            if (result.success) {
                setShowSuccess(true);
                setFormData(formularioVacio());
                setAcceptedTerms(false);
                setErrors({});

                const fileInput = document.querySelector('input[name="cv"]');
                if (fileInput) {
                    fileInput.value = '';
                }
            } else {
                setErrors({ submit: 'Error al guardar la hoja de vida' });
            }

        } catch (error) {
            console.error('Error al enviar el formulario:', error);
            setErrors({ submit: 'Error al guardar la hoja de vida' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const RequiredAsterisk = () => <span className="text-danger">*</span>;

    const nivelOptions = [
        { value: 'Básico', label: 'Básico' },
        { value: 'Intermedio', label: 'Intermedio' },
        { value: 'Avanzado', label: 'Avanzado' }
    ];

    return (
        <div className="container">
            <CustomNavbar />
            <div className="">
                <div className="card-header">
                    <h2 className="text-center mb-4">{tr('HOJA DE VIDA')}</h2>
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit} encType="multipart/form-data">

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded">
                            <h4 className="border-bottom pb-2">{tr('DATOS PERSONALES')}</h4>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Nombres')} <RequiredAsterisk /></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleInputChange}
                                    />
                                    {fe('nombre')}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Apellidos')} <RequiredAsterisk /></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.apellido ? 'is-invalid' : ''}`}
                                        name="apellido"
                                        value={formData.apellido}
                                        onChange={handleInputChange}
                                    />
                                    {fe('apellido')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('País de residencia')} <RequiredAsterisk /></label>
                                    <select
                                        className={`form-select ${errors.nacionalidad ? 'is-invalid' : ''}`}
                                        name="nacionalidad"
                                        value={formData.nacionalidad}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">{tr('Seleccionar')}</option>
                                        {paises.map((pais, index) => (
                                            <option key={index} value={pais.name}>{tr(pais.name)}</option>
                                        ))}
                                    </select>
                                    {fe('nacionalidad')}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Fecha de Nacimiento')} <RequiredAsterisk /></label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.fechaNacimiento ? 'is-invalid' : ''}`}
                                        name="fechaNacimiento"
                                        value={formData.fechaNacimiento}
                                        onChange={handleInputChange}
                                    />
                                    {fe('fechaNacimiento')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Estado Civil')} <RequiredAsterisk /></label>
                                    <select
                                        className={`form-select ${errors.estadoCivil ? 'is-invalid' : ''}`}
                                        name="estadoCivil"
                                        value={formData.estadoCivil}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">{tr('Seleccionar')}</option>
                                        <option value="Soltero">{tr('Soltero')}</option>
                                        <option value="Casado">{tr('Casado')}</option>
                                        <option value="Divorciado">{tr('Divorciado')}</option>
                                        <option value="Separado">{tr('Separado')}</option>
                                        <option value="Unión libre">{tr('Unión libre')}</option>
                                    </select>
                                    {fe('estadoCivil')}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Tipo de Documento')} <RequiredAsterisk /></label>
                                    <select
                                        className={`form-select ${errors.tipoDocumento ? 'is-invalid' : ''}`}
                                        name="tipoDocumento"
                                        value={formData.tipoDocumento}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">{tr('Seleccionar')}</option>
                                        <option value="Cédula">{tr('Cédula')}</option>
                                        <option value="DNI">{tr('DNI')}</option>
                                        <option value="Pasaporte">{tr('Pasaporte')}</option>
                                    </select>
                                    {fe('tipoDocumento')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('No. de documento')} <RequiredAsterisk /></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.cedula ? 'is-invalid' : ''}`}
                                        name="cedula"
                                        value={formData.cedula}
                                        onChange={handleInputChange}
                                    />
                                    {fe('cedula')}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Género')} <RequiredAsterisk /></label>
                                    <select
                                        className={`form-select ${errors.genero ? 'is-invalid' : ''}`}
                                        name="genero"
                                        value={formData.genero}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">{tr('Seleccionar')}</option>
                                        <option value="Femenino">{tr('Femenino')}</option>
                                        <option value="Masculino">{tr('Masculino')}</option>
                                        <option value="Otro">{tr('Otro')}</option>
                                    </select>
                                    {fe('genero')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Teléfono Celular')} <RequiredAsterisk /></label>
                                    <input
                                        type="tel"
                                        className={`form-control ${errors.telefono ? 'is-invalid' : ''}`}
                                        name="telefono"
                                        value={formData.telefono}
                                        onChange={handleInputChange}
                                    />
                                    {fe('telefono')}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Provincia (Departamento - Distrito)')} <RequiredAsterisk /></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.provincia ? 'is-invalid' : ''}`}
                                        name="provincia"
                                        value={formData.provincia}
                                        onChange={handleInputChange}
                                    />
                                    {fe('provincia')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Cantón')} <RequiredAsterisk /></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.canton ? 'is-invalid' : ''}`}
                                        name="canton"
                                        value={formData.canton}
                                        onChange={handleInputChange}
                                    />
                                    {fe('canton')}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Dirección')} <RequiredAsterisk /></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.direccion ? 'is-invalid' : ''}`}
                                        name="direccion"
                                        value={formData.direccion}
                                        onChange={handleInputChange}
                                    />
                                    {fe('direccion')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Email')} <RequiredAsterisk /></label>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                    {fe('email')}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Pretensión salarial')} <RequiredAsterisk /></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.preferenciaSalarial ? 'is-invalid' : ''}`}
                                        name="preferenciaSalarial"
                                        value={formData.preferenciaSalarial}
                                        onChange={handleInputChange}
                                    />
                                    {fe('preferenciaSalarial')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <label className="form-label">{tr('Cargo al que postula')} <span className="text-danger">*</span></label>
                                    <PuestoSelect
                                        value={formData.cargoData}
                                        onChange={handleCargoChange}
                                        error={errors.cargo}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{tr('Discapacidad')}</label>
                                    <select
                                        className={`form-select ${errors.discapacidad ? 'is-invalid' : ''}`}
                                        name="discapacidad"
                                        value={formData.discapacidad}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">{tr('Seleccionar')}</option>
                                        <option value="Sí">{tr('Sí')}</option>
                                        <option value="No">{tr('No')}</option>
                                    </select>
                                    {fe('discapacidad')}
                                </div>
                                {formData.discapacidad === 'Sí' && (
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">{tr('% Discapacidad')} <RequiredAsterisk /></label>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.porcentajeDiscapacidad ? 'is-invalid' : ''}`}
                                            name="porcentajeDiscapacidad"
                                            value={formData.porcentajeDiscapacidad}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                        />
                                        {fe('porcentajeDiscapacidad')}
                                    </div>
                                )}
                            </div>

                            <div className="col-md-12 mb-3">
                                <label className="form-label">
                                    {tr('¿Estaría dispuesto a trabajar en el exterior?')} <RequiredAsterisk />
                                </label>
                                <div className="mt-2">
                                    <div className="form-check form-check-inline">
                                        <input
                                            className={`form-check-input ${errors.trabajado_conduto ? 'is-invalid' : ''}`}
                                            type="radio"
                                            name="trabajado_conduto"
                                            id="trabajadoSi"
                                            value="si"
                                            checked={formData.trabajado_conduto === 'si'}
                                            onChange={handleRadioChange}
                                        />
                                        <label className="form-check-label" htmlFor="trabajadoSi">
                                            {tr('Sí')}
                                        </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className={`form-check-input ${errors.trabajado_conduto ? 'is-invalid' : ''}`}
                                            type="radio"
                                            name="trabajado_conduto"
                                            id="trabajadoNo"
                                            value="no"
                                            checked={formData.trabajado_conduto === 'no'}
                                            onChange={handleRadioChange}
                                        />
                                        <label className="form-check-label" htmlFor="trabajadoNo">
                                            {tr('No')}
                                        </label>
                                    </div>
                                </div>
                                {fe('trabajado_conduto', 'invalid-feedback d-block')}
                            </div>
                        </div>

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="border-bottom pb-2 mb-0">{tr('FORMACIÓN ACADÉMICA')}</h4>
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={agregarFormacion}
                                >
                                    + {tr('Agregar Formación')}
                                </button>
                            </div>

                            {formData.formacionesAcademicas.map((formacion, index) => (
                                <div key={index} className="formacion-item border p-3 mb-3 rounded">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0">{tr('Formación Académica')} #{index + 1}</h6>
                                        {formData.formacionesAcademicas.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => eliminarFormacion(index)}
                                            >
                                                × {tr('Eliminar')}
                                            </button>
                                        )}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Título / Carrera')} <RequiredAsterisk /></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors[`formacion_${index}_titulo`] ? 'is-invalid' : ''}`}
                                                value={formacion.titulo}
                                                onChange={(e) => handleFormacionChange(index, 'titulo', e.target.value)}
                                            />
                                            {fe(`formacion_${index}_titulo`)}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Tipo de Estudio')} <RequiredAsterisk /></label>
                                            <select
                                                className={`form-select ${errors[`formacion_${index}_tipoEstudio`] ? 'is-invalid' : ''}`}
                                                value={formacion.tipoEstudio}
                                                onChange={(e) => handleFormacionChange(index, 'tipoEstudio', e.target.value)}
                                            >
                                                <option value="">{tr('Seleccionar')}</option>
                                                <option value="Básico">{tr('Básico')}</option>
                                                <option value="Bachiller">{tr('Bachiller')}</option>
                                                <option value="Técnico">{tr('Técnico')}</option>
                                                <option value="Universitario">{tr('Universitario')}</option>
                                                <option value="Diplomado">{tr('Diplomado')}</option>
                                                <option value="Post grado">{tr('Post grado')}</option>
                                                <option value="Master">{tr('Master')}</option>
                                                <option value="Doctorado">{tr('Doctorado')}</option>
                                            </select>
                                            {fe(`formacion_${index}_tipoEstudio`)}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Área de Estudio')} <RequiredAsterisk /></label>
                                            <select
                                                className={`form-select ${errors[`formacion_${index}_areaEstudio`] ? 'is-invalid' : ''}`}
                                                value={formacion.areaEstudio}
                                                onChange={(e) => handleFormacionChange(index, 'areaEstudio', e.target.value)}
                                            >
                                                <option value="">{tr('Seleccionar')}</option>
                                                <option value="Ciencias de la Salud">{tr('Ciencias de la Salud')}</option>
                                                <option value="Ingeniería y Tecnología">{tr('Ingeniería y Tecnología')}</option>
                                                <option value="Ciencias Sociales y Humanidades">{tr('Ciencias Sociales y Humanidades')}</option>
                                                <option value="Negocios y Economía">{tr('Negocios y Economía')}</option>
                                                <option value="Otros">{tr('Otros')}</option>
                                            </select>
                                            {fe(`formacion_${index}_areaEstudio`)}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Institución')} <RequiredAsterisk /></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors[`formacion_${index}_institucion`] ? 'is-invalid' : ''}`}
                                                value={formacion.institucion}
                                                onChange={(e) => handleFormacionChange(index, 'institucion', e.target.value)}
                                            />
                                            {fe(`formacion_${index}_institucion`)}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Estado')} <RequiredAsterisk /></label>
                                            <select
                                                className={`form-select ${errors[`formacion_${index}_estadoEstudio`] ? 'is-invalid' : ''}`}
                                                value={formacion.estadoEstudio}
                                                onChange={(e) => handleFormacionChange(index, 'estadoEstudio', e.target.value)}
                                            >
                                                <option value="">{tr('Seleccionar')}</option>
                                                <option value="En curso">{tr('En curso')}</option>
                                                <option value="Graduado">{tr('Graduado')}</option>
                                                <option value="Abandonado">{tr('Abandonado')}</option>
                                            </select>
                                            {fe(`formacion_${index}_estadoEstudio`)}
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">{tr('Año Inicio')} <RequiredAsterisk /></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors[`formacion_${index}_fechaInicioEstudio`] ? 'is-invalid' : ''}`}
                                                value={formacion.fechaInicioEstudio}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                    handleFormacionChange(index, 'fechaInicioEstudio', value);
                                                }}
                                                maxLength="4"
                                                pattern="\d{4}"
                                                placeholder="YYYY"
                                            />
                                            {fe(`formacion_${index}_fechaInicioEstudio`)}
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">{tr('Año Fin')} <RequiredAsterisk /></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors[`formacion_${index}_fechaFinEstudio`] ? 'is-invalid' : ''}`}
                                                value={formacion.fechaFinEstudio}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                    handleFormacionChange(index, 'fechaFinEstudio', value);
                                                }}
                                                maxLength="4"
                                                pattern="\d{4}"
                                                placeholder="YYYY"
                                            />
                                            {fe(`formacion_${index}_fechaFinEstudio`)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="border-bottom pb-2 mb-0">{tr('IDIOMAS')}</h4>
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={agregarIdioma}
                                >
                                    + {tr('Agregar Idioma')}
                                </button>
                            </div>

                            {formData.idiomas.map((idioma, index) => (
                                <div key={index} className="idioma-item border p-3 mb-3 rounded">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0">{tr('Idioma')} #{index + 1}</h6>
                                        {formData.idiomas.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => eliminarIdioma(index)}
                                            >
                                                × {tr('Eliminar')}
                                            </button>
                                        )}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Idioma')} <RequiredAsterisk /></label>
                                            <select
                                                className={`form-select ${errors[`idioma_${index}_idioma`] ? 'is-invalid' : ''}`}
                                                value={idioma.idioma}
                                                onChange={(e) => handleIdiomaChange(index, 'idioma', e.target.value)}
                                            >
                                                <option value="">{tr('Seleccionar')}</option>
                                                <option value="Inglés">{tr('Inglés')}</option>
                                                <option value="Mandarin">{tr('Mandarin')}</option>
                                                <option value="Español">{tr('Español')}</option>
                                                <option value="Francés">{tr('Francés')}</option>
                                                <option value="Italiano">{tr('Italiano')}</option>
                                                <option value="Portugués">{tr('Portugués')}</option>
                                                <option value="Otros">{tr('Otros')}</option>
                                            </select>
                                            {fe(`idioma_${index}_idioma`)}
                                        </div>

                                        {idioma.idioma === "Otros" && (
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">{tr('Especifique el idioma')} <RequiredAsterisk /></label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors[`idioma_${index}_otroIdioma`] ? 'is-invalid' : ''}`}
                                                    value={idioma.otroIdioma}
                                                    onChange={(e) => handleIdiomaChange(index, 'otroIdioma', e.target.value)}
                                                    placeholder={tr('Especifique el idioma')}
                                                />
                                                {fe(`idioma_${index}_otroIdioma`)}
                                            </div>
                                        )}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Nivel Escrito')} <RequiredAsterisk /></label>
                                            <select
                                                className={`form-select ${errors[`idioma_${index}_nivelEscrito`] ? 'is-invalid' : ''}`}
                                                value={idioma.nivelEscrito}
                                                onChange={(e) => handleIdiomaChange(index, 'nivelEscrito', e.target.value)}
                                            >
                                                <option value="">{tr('Seleccionar')}</option>
                                                {nivelOptions.map(n => (
                                                    <option key={n.value} value={n.value}>{tr(n.label)}</option>
                                                ))}
                                            </select>
                                            {fe(`idioma_${index}_nivelEscrito`)}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Nivel Oral')} <RequiredAsterisk /></label>
                                            <select
                                                className={`form-select ${errors[`idioma_${index}_nivelOral`] ? 'is-invalid' : ''}`}
                                                value={idioma.nivelOral}
                                                onChange={(e) => handleIdiomaChange(index, 'nivelOral', e.target.value)}
                                            >
                                                <option value="">{tr('Seleccionar')}</option>
                                                {nivelOptions.map(n => (
                                                    <option key={n.value} value={n.value}>{tr(n.label)}</option>
                                                ))}
                                            </select>
                                            {fe(`idioma_${index}_nivelOral`)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="border-bottom pb-2 mb-0">{tr('EXPERIENCIA')}</h4>
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={agregarExperiencia}
                                >
                                    + {tr('Agregar Experiencia')}
                                </button>
                            </div>

                            <p className="ms-2 fst-italic mb-3">{tr('Colocar desde la última experiencia laboral')}</p>

                            {formData.experiencias.map((experiencia, index) => (
                                <div key={index} className="experiencia-item border p-3 mb-3 rounded">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0">{tr('Experiencia')} #{index + 1}</h6>
                                        {formData.experiencias.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => eliminarExperiencia(index)}
                                            >
                                                × {tr('Eliminar')}
                                            </button>
                                        )}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Empresa')} <RequiredAsterisk /></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors[`experiencia_${index}_empresa`] ? 'is-invalid' : ''}`}
                                                value={experiencia.empresa}
                                                onChange={(e) => handleExperienciaChange(index, 'empresa', e.target.value)}
                                            />
                                            {fe(`experiencia_${index}_empresa`)}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Actividad de la empresa')} <RequiredAsterisk /></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors[`experiencia_${index}_actividadEmpresa`] ? 'is-invalid' : ''}`}
                                                value={experiencia.actividadEmpresa}
                                                onChange={(e) => handleExperienciaChange(index, 'actividadEmpresa', e.target.value)}
                                            />
                                            {fe(`experiencia_${index}_actividadEmpresa`)}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Puesto')} <span className="text-danger">*</span></label>
                                            <PuestoSelect
                                                value={{
                                                    puesto: experiencia.puesto,
                                                    otroPuesto: experiencia.otroPuesto
                                                }}
                                                onChange={(puestoData) => handleExperienciaChange(index, 'puestoData', puestoData)}
                                                error={errors[`experiencia_${index}_puesto`]}
                                                index={index}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Nivel de experiencia')} <RequiredAsterisk /></label>
                                            <select
                                                className={`form-select ${errors[`experiencia_${index}_nivelExperiencia`] ? 'is-invalid' : ''}`}
                                                value={experiencia.nivelExperiencia}
                                                onChange={(e) => handleExperienciaChange(index, 'nivelExperiencia', e.target.value)}
                                            >
                                                <option value="">{tr('Seleccionar')}</option>
                                                <option value="Senior">{tr('Senior')}</option>
                                                <option value="Semisenior">{tr('Semisenior')}</option>
                                                <option value="Junior">{tr('Junior')}</option>
                                                <option value="Pasante">{tr('Pasante')}</option>
                                            </select>
                                            {fe(`experiencia_${index}_nivelExperiencia`)}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('País Empresa')} <RequiredAsterisk /></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors[`experiencia_${index}_paisEmpresa`] ? 'is-invalid' : ''}`}
                                                value={experiencia.paisEmpresa}
                                                onChange={(e) => handleExperienciaChange(index, 'paisEmpresa', e.target.value)}
                                            />
                                            {fe(`experiencia_${index}_paisEmpresa`)}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Personas a Cargo')} <RequiredAsterisk /></label>
                                            <input
                                                type="number"
                                                className={`form-control ${errors[`experiencia_${index}_personasCargo`] ? 'is-invalid' : ''}`}
                                                value={experiencia.personasCargo || ''}
                                                onChange={(e) => {
                                                    const value = Math.floor(Number(e.target.value));
                                                    if (value >= 0) {
                                                        handleExperienciaChange(index, 'personasCargo', value || 0);
                                                    }
                                                }}
                                                min="0"
                                                step="1"
                                                onKeyDown={(e) => {
                                                    if (e.key === "." || e.key === "e" || e.key === "-") {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            {fe(`experiencia_${index}_personasCargo`)}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Fecha inicio')} <RequiredAsterisk /></label>
                                            <div className="row">
                                                <div className="col-6">
                                                    <select
                                                        className={`form-select ${errors[`experiencia_${index}_fechaInicioMes`] ? 'is-invalid' : ''}`}
                                                        value={experiencia.fechaInicioExperiencia.mes}
                                                        onChange={(e) => handleExperienciaFechaChange(index, 'inicio', 'mes', e.target.value)}
                                                    >
                                                        <option value="">{tr('Mes')}</option>
                                                        {MESES.map(mes => (
                                                            <option key={mes.value} value={mes.value}>
                                                                {tr(mes.label)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {fe(`experiencia_${index}_fechaInicioMes`, 'invalid-feedback d-block')}
                                                </div>
                                                <div className="col-6">
                                                    <select
                                                        className={`form-select ${errors[`experiencia_${index}_fechaInicioAnio`] ? 'is-invalid' : ''}`}
                                                        value={experiencia.fechaInicioExperiencia.año}
                                                        onChange={(e) => handleExperienciaFechaChange(index, 'inicio', 'año', e.target.value)}
                                                    >
                                                        <option value="">{tr('Año')}</option>
                                                        {generarAnios().map(anio => (
                                                            <option key={anio} value={anio}>
                                                                {anio}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {fe(`experiencia_${index}_fechaInicioAnio`, 'invalid-feedback d-block')}
                                                </div>
                                            </div>
                                            {fe(`experiencia_${index}_fechaInicio`, 'text-danger small mt-1')}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">{tr('Fecha finalización')} <RequiredAsterisk /></label>
                                            <div className="row">
                                                <div className="col-6">
                                                    <select
                                                        className={`form-select ${errors[`experiencia_${index}_fechaFinMes`] ? 'is-invalid' : ''}`}
                                                        value={experiencia.fechaFinalizacionExperiencia.mes}
                                                        onChange={(e) => handleExperienciaFechaChange(index, 'fin', 'mes', e.target.value)}
                                                    >
                                                        <option value="">{tr('Mes')}</option>
                                                        {MESES.map(mes => (
                                                            <option key={mes.value} value={mes.value}>
                                                                {tr(mes.label)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {fe(`experiencia_${index}_fechaFinMes`, 'invalid-feedback d-block')}
                                                </div>
                                                <div className="col-6">
                                                    <select
                                                        className={`form-select ${errors[`experiencia_${index}_fechaFinAnio`] ? 'is-invalid' : ''}`}
                                                        value={experiencia.fechaFinalizacionExperiencia.año}
                                                        onChange={(e) => handleExperienciaFechaChange(index, 'fin', 'año', e.target.value)}
                                                    >
                                                        <option value="">{tr('Año')}</option>
                                                        {generarAnios().map(anio => (
                                                            <option key={anio} value={anio}>
                                                                {anio}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {fe(`experiencia_${index}_fechaFinAnio`, 'invalid-feedback d-block')}
                                                </div>
                                            </div>
                                            {fe(`experiencia_${index}_fechaFinalizacion`, 'text-danger small mt-1')}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">{tr('Descripción de responsabilidad')} <RequiredAsterisk /></label>
                                        <textarea
                                            className={`form-control ${errors[`experiencia_${index}_descripcionResponsabilidad`] ? 'is-invalid' : ''}`}
                                            value={experiencia.descripcionResponsabilidad}
                                            onChange={(e) => handleExperienciaChange(index, 'descripcionResponsabilidad', e.target.value)}
                                            rows="3"
                                        />
                                        {fe(`experiencia_${index}_descripcionResponsabilidad`)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded">
                            <h4 className="border-bottom pb-2">{tr('SUBIR CV')}</h4>
                            <div className="mb-3">
                                <label className="form-label">{tr('Subir Archivo CV (PDF, Word)')} <RequiredAsterisk /></label>
                                <input
                                    type="file"
                                    className={`form-control ${errors.cv ? 'is-invalid' : ''}`}
                                    name="cv"
                                    onChange={handleInputChange}
                                    accept=".pdf,.doc,.docx"
                                />
                                {fe('cv')}
                                <small className="form-text text-muted">
                                    {tr('Formatos aceptados: PDF, DOC, DOCX. Tamaño máximo: 5MB')}
                                </small>
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="box mt-2 p-2 text-content" style={{ backgroundColor: "#f8f9fa", border: "1px solid #ced4da", borderRadius: "5px" }}>
                                {tr('La información transmitida, incluidos los archivos adjuntos, está destinada únicamente a la(s) persona(s) o entidad a la que se dirige y puede contener material confidencial y/o privilegiado. Queda prohibida cualquier revisión, retransmisión, difusión u otro uso, o cualquier acción basada en esta información por parte de personas o entidades que no sean el destinatario previsto. Para acceder a nuestra Política de Tratamiento de Datos Personales')}{' '}
                                <a href="https://www.conduto.com/images/dp/POLITICA-DE-TRATAMIENTO-DE-DATOS-PERSONALES.pdf" target="_blank" rel="noopener noreferrer">{tr('aquí')}</a>
                                {tr(', y si desea comunicarse con nosotros tenga a su disposición el correo:')}{' '}
                                <a href="mailto:datospersonales@conduto.com">datospersonales@conduto.com</a>
                            </div>
                            <br />
                            <div className="form-check">
                                <input
                                    style={{
                                        width: "18px",
                                        height: "18px",
                                        border: "2px solid #014993",
                                        backgroundColor: acceptedTerms ? "#014993" : "#eee",
                                        appearance: "none",
                                        cursor: "pointer"
                                    }}
                                    className={`form-check-input ${errors.terms ? 'is-invalid' : ''}`}
                                    type="checkbox"
                                    id="termsCheck"
                                    checked={acceptedTerms}
                                    onChange={(e) => {
                                        setAcceptedTerms(e.target.checked);
                                        if (errors.terms) {
                                            setErrors(prev => ({ ...prev, terms: '' }));
                                        }
                                    }}
                                />
                                <label className="form-check-label" htmlFor="termsCheck">
                                    {tr('Autorizo el tratamiento de mis datos personales a Conduto Ecuador S.A. conforme lo establecido en su política de privacidad')} <RequiredAsterisk />
                                </label>
                                {fe('terms', 'invalid-feedback d-block')}
                            </div>
                        </div>

                        <div className="d-grid gap-2">
                            {showSuccess && (
                                <SuccessMessage
                                    message={tr('Hoja de vida guardada correctamente')}
                                    onClose={() => setShowSuccess(false)}
                                />
                            )}

                            {errors.submit && <ErrorMessage message={tr(errors.submit)} />}
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                disabled={isSubmitting || !acceptedTerms}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {tr('Guardando...')}
                                    </>
                                ) : (
                                    tr('Guardar Hoja de Vida')
                                )}
                            </button>
                        </div>
                        <br />
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Formulario;